import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Public account-deletion page (app-store requirement) + self-service deletion
   for signed-in members. */
export default function DeleteAccount() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Delete your account | TravelerPulse.pro"; }, []);

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account");
      setDone(true);
      toast.success("Your account has been deleted");
      await logout();
      setTimeout(() => nav("/"), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed — contact support@travelerpulse.pro");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-12" data-testid="delete-account-page">
      <h1 className="font-heading font-900 text-3xl mb-4 flex items-center gap-3">
        <Trash2 className="w-8 h-8 text-red-600" /> Delete your TravelerPulse account
      </h1>
      <div className="bg-white border border-zinc-300 p-6 text-sm leading-relaxed space-y-3">
        <p>Deleting your account is <b>permanent</b>. When you delete your account we:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Permanently remove your sign-in and profile</li>
          <li>Delete your trips, stays, expenses, saved flights, reminders, alerts and other personal data</li>
          <li>Anonymize your community posts and photos (shown as "Deleted traveler")</li>
        </ul>
        <p>
          You can delete your account here on the website (below) or from the mobile app under
          Settings → Account → Delete account. Questions? Email{" "}
          <a href="mailto:support@travelerpulse.pro" className="text-[#1a73e8] underline">support@travelerpulse.pro</a>.
          See also our <Link to="/legal/privacy" className="text-[#1a73e8] underline">Privacy Policy</Link>.
        </p>
      </div>

      {done ? (
        <div className="mt-6 bg-green-50 border border-green-300 p-5 text-sm font-600 text-green-800"
          data-testid="delete-account-done">
          Your account and personal data have been deleted. Safe travels!
        </div>
      ) : loading ? null : !user ? (
        <div className="mt-6 bg-zinc-50 border border-zinc-300 p-5 text-sm">
          To delete your account, please{" "}
          <Link to="/login?next=/delete-account" className="text-[#1a73e8] font-700 underline"
            data-testid="delete-account-signin">sign in first</Link>.
        </div>
      ) : (
        <div className="mt-6 bg-red-50 border border-red-300 p-5" data-testid="delete-account-confirm-box">
          <div className="flex items-center gap-2 text-red-700 font-800 text-sm mb-3">
            <AlertTriangle className="w-4 h-4" /> This cannot be undone
          </div>
          <p className="text-sm mb-3">Signed in as <b>{user.email}</b>. Type <b>DELETE</b> to confirm:</p>
          <div className="flex gap-2 max-w-sm">
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE"
              className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-red-500"
              data-testid="delete-account-confirm-input" />
            <button onClick={doDelete} disabled={confirm !== "DELETE" || busy}
              data-testid="delete-account-submit"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-800 hover:opacity-90 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete forever
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
